import { useTranslation } from "react-i18next";
import { App } from "antd";

import BaseButton from "@/components_v2/Button/BaseButton";
import {
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import { useResendCertificationInvitationMutation } from "@/redux/service";
import {
  ADMIN_TABS,
  certificationPaths,
} from "@/domains/certifications/routes";
import type { AdminOriginState } from "@/domains/certifications/types";
import { InvitationActionProps } from "@/features/certifications/invitations/types";
import { CERTIFICATION_INVITATION_STATUSES } from "@/domains/certifications/constants";

const FROM_INVITATIONS: AdminOriginState = {
  fromTab: ADMIN_TABS.INVITATIONS,
};

const InvitationAction = ({
  certificationSlug,
  routeKey,
  invitation,
}: InvitationActionProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const [resend, { isLoading }] = useResendCertificationInvitationMutation();

  if (invitation.status === CERTIFICATION_INVITATION_STATUSES.ENROLLED) {
    return invitation.user === null ? null : (
      <BaseButton
        type={BUTTON_TYPES.LINK}
        variant={BUTTON_VARIANTS.LINK}
        size={BUTTON_SIZES.SMALL}
        to={certificationPaths.participant(routeKey, invitation.user.id)}
        state={FROM_INVITATIONS}
        ariaLabel={t("certification.admin.invitations.viewParticipantLabel", {
          email: invitation.email,
        })}
      >
        {t("certification.admin.invitations.viewParticipant")}
      </BaseButton>
    );
  }

  const handleResend = async () => {
    try {
      await resend({
        certificationSlug,
        invitationId: invitation.id,
      }).unwrap();
      notification.success({
        message: t("certification.admin.invitations.resent", {
          email: invitation.email,
        }),
      });
    } catch {
      notification.error({
        message: t("certification.admin.invitations.resendError"),
      });
    }
  };

  return (
    <BaseButton
      variant={BUTTON_VARIANTS.LINK}
      size={BUTTON_SIZES.SMALL}
      loading={isLoading}
      onClick={handleResend}
      ariaLabel={t("certification.admin.invitations.resendLabel", {
        email: invitation.email,
      })}
    >
      {t("certification.admin.invitations.resend")}
    </BaseButton>
  );
};

export default InvitationAction;
